import {
  getForegroundPermissionsAsync,
  hasServicesEnabledAsync,
  PermissionStatus,
  requestForegroundPermissionsAsync,
} from 'expo-location';
import { useCallback, useEffect, useState } from 'react';

export type LocationAccess =
  | 'checking'
  | 'servicesOff'
  | 'permissionDenied'
  | 'granted';

interface UseLocationServicesResult {
  access: LocationAccess;
  checkLocationServices: () => Promise<void>;
  requestPermission: () => Promise<boolean>;
  hasPermission: boolean;
}

export const useLocationServices = (): UseLocationServicesResult => {
  const [access, setAccess] = useState<LocationAccess>('checking');
  const [hasPermission, setHasPermission] = useState<boolean>(false);

  const checkLocationServices = useCallback(async () => {
    try {
      const { status } = await getForegroundPermissionsAsync();
      const permissionGranted = status === PermissionStatus.GRANTED;
      setHasPermission(permissionGranted);
      const isEnabled = await hasServicesEnabledAsync();

      if (!isEnabled) {
        setAccess('servicesOff');
        return;
      }

      if (permissionGranted) {
        setAccess('granted');
        return;
      }

      setAccess('permissionDenied');
    } catch {
      setAccess('permissionDenied');
      setHasPermission(false);
    }
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      const { status } = await requestForegroundPermissionsAsync();

      const granted = status === PermissionStatus.GRANTED;
      setHasPermission(granted);

      await checkLocationServices();

      return granted;
    } catch {
      return false;
    }
  }, [checkLocationServices]);

  useEffect(() => {
    void checkLocationServices();
  }, [checkLocationServices]);

  return {
    access,
    checkLocationServices,
    requestPermission,
    hasPermission,
  };
};
